// Slim dismissable banner that nudges anonymous users to sign in once they've
// accumulated enough progress that losing it would hurt. Triggers per spec:
// 10+ saved words OR a streak of 3+. After dismiss, never auto-shows again
// (only via Settings → Sign in).

import { CloudUpload, X } from 'lucide-react';
import { C } from '@/styles/tokens';

interface SignInNudgeProps {
  /** Reason text decided by the parent based on which threshold tripped. */
  reason: string;
  onSignIn: () => void;
  onDismiss: () => void;
}

export function SignInNudge({ reason, onSignIn, onDismiss }: SignInNudgeProps) {
  return (
    <div
      className="mx-4 mt-2 p-3 rounded-2xl flex items-center gap-3 anim-slide-up"
      role="region"
      aria-label="Sign-in suggestion"
      style={{
        background: C.blush,
        boxShadow: '0 2px 8px rgba(233,75,60,0.15)',
      }}
    >
      <span
        aria-hidden
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: 'white' }}
      >
        <CloudUpload size={16} aria-hidden style={{ color: C.vermillion }} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="ff-body text-[12px] font-semibold leading-tight inline-flex items-baseline gap-1.5" style={{ color: C.vermillionDeep }}>
          Save your progress
          <span className="ff-zh-serif opacity-80 font-normal">· 保存进度</span>
        </div>
        <div className="ff-body text-[11px] leading-snug mt-0.5 truncate" style={{ color: C.inkLight }}>
          {reason}
        </div>
      </div>
      <button
        type="button"
        onClick={onSignIn}
        className="px-3 py-1.5 rounded-full ff-body text-[10px] uppercase tracking-[0.18em] font-bold text-white flex-shrink-0 inline-flex items-center gap-1"
        style={{ background: C.vermillion }}
      >
        Sign in
        <span className="ff-zh-serif tracking-normal opacity-90">登录</span>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss sign-in nudge"
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: 'rgba(255,255,255,0.7)', color: C.inkLight }}
      >
        <X size={12} aria-hidden />
      </button>
    </div>
  );
}
